import App from 'next/app'
import type { AppContext, AppProps } from 'next/app'
import { NextComponentType, NextPageContext } from 'next'
import 'src/styles/tailwind.css'
import 'eyecons-components/dist/index.css'
import Head from 'next/head'
import React from 'react'
import Script from 'next/script'
import { runtimeConfig, RuntimeConfigContext, type RuntimeConfig } from 'src/config'
import { RequestContext } from 'eyecons-requests'

type CustomComponentType = NextComponentType<NextPageContext, any, any> & {
    getLayout?(page: React.ReactNode): React.ReactNode
}

function Eyecons({ Component, pageProps, runtimeConfig }: AppProps & { runtimeConfig: RuntimeConfig }) {
    // Use the layout defined at the page level, if available
    const getLayout = (Component as CustomComponentType).getLayout || ((page) => page)

    return (
        <>
            <Head>
                <script src={`https://cdn.jwplayer.com/libraries/${runtimeConfig.jwPlayer.playerId}.js`} />
                <title>EYECONS: Volg jouw favoriete sport bij Eyecons, voor de sport, voor de fans</title>
                <meta name={'description'} content={'Op Eyecons.com vind je Live en On Demand video van topclubs in onder andere Voetbal, Hockey, Korfbal, Honkbal, Volleybal, Handbal en Rugby.'} />
                <meta name="robots" content={runtimeConfig.eyecons.env === 'prod' ? 'index' : 'noindex,nofollow'} />
                <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
                <link rel="icon" type="image/png" sizes="64x64" href={`${runtimeConfig.eyecons.urls.cdn}/front-end/assets/head/favicon-64x64.png`} />
                <link rel="icon" type="image/png" sizes="32x32" href={`${runtimeConfig.eyecons.urls.cdn}/front-end/assets/head/favicon-32x32.png`} />
                <link rel="icon" type="image/png" sizes="16x16" href={`${runtimeConfig.eyecons.urls.cdn}/front-end/assets/head/favicon-16x16.png`} />
                <link rel="apple-touch-icon" sizes="180x180" href={`${runtimeConfig.eyecons.urls.cdn}/front-end/assets/head/apple-touch-icon.png`} />
                <link rel="manifest" href="/manifest.json" />
                <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
                <meta name="apple-mobile-web-app-capable" content="yes" />
                <meta name="apple-mobile-web-app-title" content="Eyecons" />
                <meta name="mobile-web-app-capable" content="yes" />
                <meta name="apple-itunes-app" content="app-id=6451410028" />
                <meta name="google-play-app" content="app-id=com.eyecons" />
                <link rel="mask-icon" href={`${runtimeConfig.eyecons.urls.cdn}/front-end/assets/head/safari-pinned-tab.svg`} />
                <link rel="shortcut icon" href={`${runtimeConfig.eyecons.urls.cdn}/front-end/assets/head/favicon.ico`} />
                <meta name="msapplication-TileColor" content="#14a2d2" />
                <meta name="msapplication-config" content={`${runtimeConfig.eyecons.urls.cdn}/front-end/assets/head/browserconfig.xml`} />
                <meta name="theme-color" content="#ffffff" />
                <link rel={'canonical'} href={runtimeConfig.eyecons.urls.site} />
            </Head>

            {/* Google Analytics */}
            {runtimeConfig.ga.tagId && (
                <>
                    <Script strategy="afterInteractive" src={`https://www.googletagmanager.com/gtag/js?id=${runtimeConfig.ga.tagId}`} />
                    <Script
                        id="google-analytics"
                        strategy="afterInteractive"
                        dangerouslySetInnerHTML={{
                            __html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        gtag('config', '${runtimeConfig.ga.tagId}', {
                            page_path: window.location.pathname,
                        });
                    `,
                        }}
                    />
                </>
            )}

            <RuntimeConfigContext.Provider value={runtimeConfig}>
                <RequestContext.Provider value={runtimeConfig.eyecons.urls}>{getLayout(<Component {...pageProps} />)}</RequestContext.Provider>
            </RuntimeConfigContext.Provider>

            <Script src="https://mmcdn.nl/tags/eyecons.com/home.js" async />

            <Script
                dangerouslySetInnerHTML={{
                    __html: `let deferredPrompt;

                    window.addEventListener('beforeinstallprompt', (e) => {
                        e.preventDefault();
                        deferredPrompt = e;
                    });`,
                }}
            />
        </>
    )
}

Eyecons.getInitialProps = async (appContext: AppContext) => {
    const appProps = await App.getInitialProps(appContext)

    return {
        ...appProps,
        runtimeConfig,
    }
}

export default Eyecons
